import React from "react";
import { Icon } from "@iconify/react";
import SelectSide from "../../components/NewOrder/SelectSide";
import OrderType from "../../components/NewOrder/OrderType";
import PriceTemplate from "../OrderTemplates/PriceTemplate";
import QuantityTemplate from "../OrderTemplates/QuantityTemplate";
import TrailingTemplate from "../OrderTemplates/TrailingTemplate";
import MarginSelect from "../../components/MarginSelect/MarginSelect";
import SwitchInput from "../../components/SwitchInput/SwitchInput";
import ConditionsArea from "../Automations/AutomationModal/ConditionsArea/ConditionsArea";
import ActionsArea from "../Automations/AutomationModal/ActionsArea/ActionsArea";
import { MARKET_TYPES, STOP_TYPES } from "../../services/ExchangeService";
import { filterOrderTemplatesForPair } from "./automationSetupUtils";

export function SetupField({ label, htmlFor, hint, children, className = "" }) {
    return (
        <div className={`unified-setup-field ${className}`.trim()}>
            {label ? <label htmlFor={htmlFor} className="unified-setup-label">{label}</label> : null}
            {children}
            {hint ? <p className="unified-setup-field-hint">{hint}</p> : null}
        </div>
    );
}

export function SetupSection({ icon, title, subtitle, children }) {
    return (
        <section className="unified-setup-card">
            <div className="unified-setup-card-header">
                <span className="unified-setup-card-icon" aria-hidden="true">
                    <Icon icon={icon} width={20} height={20} />
                </span>
                <div>
                    <h2 className="unified-setup-card-title">{title}</h2>
                    {subtitle ? <p className="unified-setup-card-text">{subtitle}</p> : null}
                </div>
            </div>
            {children}
        </section>
    );
}

function getPriceClasses(orderType) {
    if (!orderType) return "col-12 mb-3 d-none";
    return MARKET_TYPES.includes(orderType) ? "col-12 mb-3 d-none" : "col-12 mb-3";
}

function getStopPriceClasses(orderType) {
    return STOP_TYPES.includes(orderType) ? "col-12 mb-3" : "col-12 mb-3 d-none";
}

export function OrderTemplateColumn({
    label,
    side,
    savedId,
    orderData,
    orderTemplates,
    market,
    setupSymbol,
    onSelectExisting,
    onChange,
}) {
    const usingExisting = !!savedId;
    const ordersForPair = filterOrderTemplatesForPair(orderTemplates, setupSymbol, {
        side: side.toUpperCase(),
        market,
    });

    return (
        <div className={`col-md-6 unified-setup-column unified-setup-column--${side}`}>
            <h3 className="unified-setup-column-title">{label}</h3>

            <SetupField
                label="Use existing or create new"
                hint={setupSymbol ? `Showing ${market.toLowerCase()} order templates for ${setupSymbol}.` : "Select a trading pair first."}
            >
                <select
                    className="form-select"
                    value={savedId || ""}
                    onChange={onSelectExisting}
                    disabled={!setupSymbol}
                >
                    <option value="">Create new</option>
                    {ordersForPair.map((order) => (
                        <option key={order.id} value={order.id}>{order.name}</option>
                    ))}
                </select>
            </SetupField>

            {!usingExisting ? (
                <>
                    <SetupField label="Name" htmlFor={`${side}-order-name`}>
                        <input id={`${side}-order-name`} type="text" className="form-control" value={orderData.name} onChange={onChange} />
                    </SetupField>

                    <div className="row">
                        <div className="col-12 mb-3">
                            <SelectSide side={orderData.side} onChange={onChange} isFuture={market === "FUTURES"} />
                        </div>
                        <div className="col-12 mb-3">
                            <OrderType type={orderData.type} onChange={onChange} isFuture={market === "FUTURES"} />
                        </div>
                    </div>

                    {market === "FUTURES" ? (
                        <div className="row">
                            <div className="col-12">
                                <SetupField label="Margin type">
                                    <MarginSelect marginType={orderData.marginType} onChange={onChange} />
                                </SetupField>
                            </div>
                            <div className="col-12">
                                <SetupField label="Leverage" htmlFor={`${side}-leverage`}>
                                    <div className="input-group">
                                        <input id="leverage" type="number" className="form-control" value={orderData.leverage || ""} onChange={onChange} />
                                        <span className="input-group-text">x</span>
                                    </div>
                                </SetupField>
                            </div>
                            <div className="col-12">
                                <SwitchInput id="reduceOnly" text="Reduce only?" isChecked={orderData.reduceOnly} onChange={onChange} />
                            </div>
                        </div>
                    ) : null}

                    {(orderData.type ? String(orderData.type) : "").startsWith("TRAILING_STOP") ? (
                        <TrailingTemplate data={orderData} onChange={onChange} isFuture={market === "FUTURES"} />
                    ) : (
                        <div className="row">
                            <div className={getPriceClasses(orderData.type)}>
                                <PriceTemplate
                                    id="limitPrice"
                                    text="Limit price"
                                    isFuture={market === "FUTURES"}
                                    symbol={setupSymbol}
                                    onChange={onChange}
                                    price={orderData.limitPrice}
                                    multiplier={orderData.limitPriceMultiplier}
                                />
                            </div>
                            <div className={getStopPriceClasses(orderData.type)}>
                                <PriceTemplate
                                    id="stopPrice"
                                    text="Stop price"
                                    isFuture={market === "FUTURES"}
                                    symbol={setupSymbol}
                                    onChange={onChange}
                                    price={orderData.stopPrice}
                                    multiplier={orderData.stopPriceMultiplier}
                                />
                            </div>
                        </div>
                    )}

                    <div className="row mt-2">
                        <div className="col-md-12">
                            <QuantityTemplate
                                id="quantity"
                                text="Order size"
                                symbol={setupSymbol}
                                quantity={orderData.quantity}
                                multiplier={orderData.quantityMultiplier}
                                onChange={onChange}
                            />
                        </div>
                    </div>

                </>
            ) : (
                <p className="unified-setup-field-hint">Using an existing order template. Switch to &quot;Create new&quot; to define a different one.</p>
            )}
        </div>
    );
}

export function BotLogicColumn({
    label,
    side,
    logicData,
    setupSymbol,
    market,
    automationIndexes,
    onChange,
}) {
    return (
        <div className={`col-md-6 unified-setup-column unified-setup-column--${side}`}>
            <h3 className="unified-setup-column-title">{label}</h3>

            <SetupField label="Name" htmlFor={`${side}-logic-name`}>
                <input
                    id={`${side}-logic-name`}
                    type="text"
                    className="form-control"
                    value={logicData.name}
                    onChange={(e) => onChange("name", e.target.value)}
                />
            </SetupField>
            <SetupField
                label="Conditions"
                hint={
                    setupSymbol
                        ? `Showing ${String(market || 'SPOT').toLowerCase()} indicators for ${setupSymbol}.`
                        : 'Select a trading pair first.'
                }
            >
                <ConditionsArea
                    symbol={setupSymbol}
                    market={market}
                    conditions={logicData.conditions}
                    indexes={automationIndexes}
                    onChange={onChange}
                />
            </SetupField>
            <SetupField
                label="Actions"
                hint={`These actions run when ${side} conditions are met.`}
            >
                <ActionsArea
                    symbol={setupSymbol}
                    orderSide={side === 'buy' ? 'BUY' : 'SELL'}
                    actions={logicData.actions || []}
                    onChange={onChange}
                />
            </SetupField>
        </div>
    );
}